import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ScanBarcode, 
  Camera, 
  Upload, 
  Keyboard, 
  CheckCircle2, 
  AlertCircle,
  Loader2,
  Package,
  Tag,
  DollarSign,
  History,
  BarChart2,
  RefreshCw,
  Layers,
  ImagePlus,
  Images,
  Video,
  Bluetooth,
  Zap
} from 'lucide-react';
import RetailerLayout from '../RetailerLayout';
import api from '../../../utils/api';
import ProductEntryForm from './ProductEntryForm';
import BatchScanner from './BatchScanner';
import ShelfScanUpload from './ShelfScanUpload';
import MultiImageUpload from './MultiImageUpload';
import ContinuousScanMode from './ContinuousScanMode';
import RapidScanInput from './RapidScanInput';
import VideoScanUpload from './VideoScanUpload';
import BatchConfirmationGrid from './BatchConfirmationGrid';
// Modular Components
import ScanModeSelector from './ScanModeSelector';
import ScanQueue from './ScanQueue';
import ScanErrorAlert from './ScanErrorAlert';
// GuidedWalkthrough now managed by RetailerLayout - no auto-show, user triggers via Tutorial button
import { useLanguage } from '../../../contexts/LanguageContext';
import { toast } from 'sonner';
import { HelpTooltip } from '../../shared/HelpTooltip';

// Demo Mode sample products for quick demos
const DEMO_PRODUCTS = [
  {
    raw_code: '4011',
    barcode_type: 'PLU',
    parsed: {
      raw_code: '4011',
      format: 'PLU',
      product_name: 'Banana',
      category: 'Fruits',
      confidence: 0.9,
      source: 'plu_database'
    },
    confidence: 0.9,
    source: 'demo',
    ready_to_post: true,
    needs_input: false,
    name: 'Banana',
    category: 'Fruits',
    regular_price: 0.59,
    quantity: 25,
    discount_level: 2
  },
  {
    raw_code: '4016',
    barcode_type: 'PLU',
    parsed: {
      raw_code: '4016',
      format: 'PLU',
      product_name: 'Red Delicious Apple',
      category: 'Fruits',
      confidence: 0.9,
      source: 'plu_database'
    },
    confidence: 0.9,
    source: 'demo',
    ready_to_post: true,
    needs_input: false,
    name: 'Red Delicious Apple',
    category: 'Fruits',
    regular_price: 1.29,
    quantity: 20,
    discount_level: 2
  },
  {
    raw_code: '041270116695',
    barcode_type: 'UPC-A',
    parsed: {
      raw_code: '041270116695',
      format: 'UPC-A',
      product_name: '365 Organic 2% Reduced Fat Milk',
      category: 'Dairy & Eggs',
      brand: '365 by Whole Foods Market',
      confidence: 0.85,
      source: 'store_registry'
    },
    confidence: 0.85,
    source: 'demo',
    ready_to_post: true,
    needs_input: false,
    name: '365 Organic 2% Reduced Fat Milk',
    category: 'Dairy & Eggs',
    regular_price: 4.99,
    quantity: 15,
    discount_level: 2
  }
];

export default function BarcodeScanner({ user, onLogout }) {
  const location = useLocation();
  const { t, isSpanish } = useLanguage();
  
  const [activeTab, setActiveTab] = useState('single'); // 'single', 'batch'
  const [mode, setMode] = useState('scan'); // 'scan', 'camera', 'manual', 'result', 'form', 'batch-confirm'
  const [barcode, setBarcode] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cameraStarted, setCameraStarted] = useState(false);
  const [recentScans, setRecentScans] = useState([]);
  const [scanStats, setScanStats] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [demoModeActive, setDemoModeActive] = useState(false);
  
  // Guided Walkthrough
  // Tutorial is now triggered from RetailerLayout's Tutorial button - no auto-show
  
  // Single scan queue - minimum 2 items required
  const [scanQueue, setScanQueue] = useState([]);
  const MIN_ITEMS_REQUIRED = 2;
  const [showOnboarding, setShowOnboarding] = useState(true);
  
  const html5QrCodeRef = useRef(null);
  const scannerContainerRef = useRef(null);

  // Handle repost from dashboard
  useEffect(() => {
    if (location.state?.repostData) {
      const repostData = location.state.repostData;
      setBarcode(repostData.raw_code);
      // Auto-scan the repost barcode
      handleScanBarcode(repostData.raw_code);
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Fetch recent scans and stats on mount
  useEffect(() => {
    fetchRecentScans();
    fetchScanStats();
  }, []);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const fetchRecentScans = async () => {
    try {
      const response = await api.get('/barcode/history?limit=10');
      if (response.data.success) {
        setRecentScans(response.data.scans);
      }
    } catch (err) {
      console.error('Failed to fetch recent scans:', err);
    }
  };

  const fetchScanStats = async () => {
    try {
      const response = await api.get('/barcode/history/stats?days=7');
      if (response.data.success) {
        setScanStats(response.data.stats);
        // Tutorial auto-show removed - now triggered via Tutorial button in nav
      }
    } catch (err) {
      console.error('Failed to fetch scan stats:', err);
    }
  };

  const handleQuickRescan = async (code) => {
    setBarcode(code);
    await handleScanBarcode(code);
  };

  const handleScanBarcode = useCallback(async (code) => {
    if (!code || code.length < 4) {
      setError('Please enter a valid barcode (at least 4 characters)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/barcode/scan', {
        code: code.trim(),
        include_lookup: true
      });

      if (response.data.success) {
        setScanResult(response.data.barcode);
        setMode('result');
        // Refresh recent scans
        fetchRecentScans();
      } else {
        setError('Barcode scan failed');
      }
    } catch (err) {
      console.error('Barcode scan error:', err);
      setError(err.response?.data?.detail || 'Failed to scan barcode');
    } finally {
      setLoading(false);
    }
  }, []);

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      
      if (!scannerContainerRef.current) return;
      
      html5QrCodeRef.current = new Html5Qrcode('barcode-scanner');
      
      await html5QrCodeRef.current.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 150 },
          aspectRatio: 1.7778
        },
        (decodedText) => {
          // Barcode detected
          setBarcode(decodedText);
          stopCamera();
          handleScanBarcode(decodedText);
        },
        (errorMessage) => {
          // Ignore scanning errors (they're normal during scanning)
        }
      );
      
      setCameraStarted(true);
    } catch (err) {
      console.error('Camera start error:', err);
      setError('Failed to start camera. Please check camera permissions.');
      setCameraStarted(false);
    }
  }, [handleScanBarcode]);

  const stopCamera = useCallback(async () => {
    if (html5QrCodeRef.current && cameraStarted) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (err) {
        console.error('Camera stop error:', err);
      }
      setCameraStarted(false);
    }
  }, [cameraStarted]);

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/barcode/scan-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success && response.data.barcodes.length > 0) {
        const firstBarcode = response.data.barcodes[0];
        setBarcode(firstBarcode.raw_code);
        setScanResult(firstBarcode.parsed);
        setMode('result');
      } else {
        setError('No barcode detected in image');
      }
    } catch (err) {
      console.error('Image upload error:', err);
      setError(err.response?.data?.detail || 'Failed to process image');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setMode('scan');
    setScanResult(null);
    setBarcode('');
    setError(null);
    fetchRecentScans();
    fetchScanStats();
  };

  // Add scanned item to queue
  const handleAddToQueue = () => {
    if (!scanResult) return;
    
    // Check if already in queue (by barcode)
    const existingIndex = scanQueue.findIndex(item => item.raw_code === scanResult.raw_code);
    if (existingIndex >= 0) {
      toast.info(t('scanner_ui.item_already_in_queue', 'This item is already in the queue'));
      handleReset();
      return;
    }
    
    const queueItem = {
      raw_code: scanResult.raw_code || barcode,
      barcode_type: scanResult.format || 'Unknown',
      parsed: scanResult,
      confidence: scanResult.confidence || 0.5,
      source: scanResult.source || 'barcode',
      ready_to_post: scanResult.product_name !== null,
      needs_input: !scanResult.product_name,
      name: scanResult.product_name || '',
      category: scanResult.category || '',
      regular_price: scanResult.price || '',
      quantity: 10,
      discount_level: 2
    };
    
    setScanQueue(prev => [...prev, queueItem]);
    toast.success(t('scanner_ui.added_to_queue', `Added to queue (${scanQueue.length + 1}/${MIN_ITEMS_REQUIRED} minimum)`));
    handleReset();
  };

  // Remove item from queue
  const handleRemoveFromQueue = (index) => {
    setScanQueue(prev => prev.filter((_, i) => i !== index));
  };

  // Clear entire queue
  const handleClearQueue = () => {
    setScanQueue([]);
    setDemoModeActive(false);
    toast.info(t('scanner_ui.queue_cleared', 'Queue cleared'));
  };

  // Activate Demo Mode - pre-fill queue with sample products
  const activateDemoMode = () => {
    setScanQueue(DEMO_PRODUCTS);
    setDemoModeActive(true);
    setMode('scan');
    toast.success('Demo Mode activated! 3 sample products added to queue. Click "Review & Post" to continue.', {
      duration: 5000,
      icon: '🎬'
    });
  };

  // Submit batch from queue
  const handleSubmitQueue = async (items) => {
    if (items.length < MIN_ITEMS_REQUIRED) {
      toast.error(t('scanner_ui.min_items_error', `Minimum ${MIN_ITEMS_REQUIRED} items required`));
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/barcode/batch-create-rshd', { 
        items,
        scan_mode: demoModeActive ? 'demo' : 'single'
      });
      
      if (response.data.success) {
        toast.success(t('scanner_ui.batch_success', `Successfully posted ${response.data.created_count} deals!`));
        setScanQueue([]);
        setDemoModeActive(false);
        setMode('scan');
        fetchRecentScans();
        fetchScanStats();
      } else {
        toast.error(response.data.message || 'Failed to create deals');
      }
    } catch (err) {
      console.error('Batch submit error:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit batch');
    } finally {
      setLoading(false);
    }
  };

  // Render recent scans sidebar
  const renderRecentScans = () => (
    <Card className="h-fit">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <History className="w-4 h-4" />
          {t('scanner_ui.recent_scans', 'Recent Scans')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {recentScans.length === 0 ? (
          <p className="text-sm text-gray-500">{t('scanner_ui.no_recent_scans', 'No recent scans')}</p>
        ) : (
          <div className="space-y-2">
            {recentScans.slice(0, 5).map((scan, index) => (
              <button
                key={index}
                onClick={() => handleQuickRescan(scan.raw_code)}
                className="w-full text-left p-2 rounded hover:bg-gray-50 transition-colors"
                data-testid={`recent-scan-${scan.raw_code}`}
              >
                <div className="flex justify-between items-center">
                  <span className="font-mono text-sm">{scan.raw_code}</span>
                  <Badge variant="outline" className="text-xs">{scan.format}</Badge>
                </div>
                {scan.product_name && (
                  <p className="text-xs text-gray-500 truncate">{scan.product_name}</p>
                )}
              </button>
            ))}
          </div>
        )}
        {recentScans.length > 5 && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full mt-2"
            onClick={() => setShowHistory(true)}
          >
            {t('scanner_ui.view_all', 'View All')} ({recentScans.length})
          </Button>
        )}
      </CardContent>
    </Card>
  );

  // Render scan stats
  const renderScanStats = () => {
    if (!scanStats) return null;
    
    return (
      <Card className="h-fit">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <BarChart2 className="w-4 h-4" />
            {t('scanner_ui.this_week', 'This Week')}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 gap-2 text-center">
            <div className="p-2 bg-blue-50 rounded">
              <p className="text-2xl font-bold text-blue-600">{scanStats.total_scans}</p>
              <p className="text-xs text-gray-500">{t('scanner_ui.scans', 'Scans')}</p>
            </div>
            <div className="p-2 bg-green-50 rounded">
              <p className="text-2xl font-bold text-green-600">{scanStats.rshd_created}</p>
              <p className="text-xs text-gray-500">{t('scanner_ui.rshds', 'RSHDs')}</p>
            </div>
          </div>
          {scanStats.top_categories.length > 0 && (
            <div className="mt-2 pt-2 border-t">
              <p className="text-xs text-gray-500 mb-1">{t('scanner_ui.top_categories', 'Top Categories:')}</p>
              {scanStats.top_categories.slice(0, 2).map((cat, i) => (
                <Badge key={i} variant="secondary" className="mr-1 text-xs">
                  {cat.category} ({cat.count})
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderScanMode = () => (
    <div className="space-y-6">
      {/* Mode Selection using modular ScanModeSelector */}
      <ScanModeSelector
        mode={mode}
        onModeChange={(newMode) => {
          if (newMode === 'scan') stopCamera();
          setMode(newMode);
        }}
        showOnboarding={showOnboarding}
        onDismissOnboarding={() => setShowOnboarding(false)}
        isNewUser={scanStats?.total_scans < 5}
        t={t}
      />

      {/* Keyboard Input Mode */}
      {mode === 'scan' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ScanBarcode className="w-5 h-5" />
              {t('scanner_ui.enter_barcode', 'Enter Barcode')}
            </CardTitle>
            <CardDescription>
              {t('scanner_ui.barcode_scanner_desc', 'Type the barcode number or use a USB barcode scanner')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              type="text"
              placeholder={t('scanner_ui.barcode_placeholder', 'Enter barcode (UPC, EAN, PLU...)')}
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleScanBarcode(barcode)}
              autoFocus
              data-testid="barcode-input"
              className="text-lg font-mono"
            />
            <Button 
              onClick={() => handleScanBarcode(barcode)}
              disabled={loading || !barcode}
              className="w-full"
              data-testid="scan-button"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('scanner_ui.scanning', 'Scanning...')}</>
              ) : (
                <><ScanBarcode className="w-4 h-4 mr-2" /> {t('scanner_ui.scan_barcode', 'Scan Barcode')}</>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Camera Mode */}
      {mode === 'camera' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              {t('scanner_ui.camera_scanner', 'Camera Scanner')}
            </CardTitle>
            <CardDescription>
              {t('scanner_ui.camera_scanner_desc', 'Point your camera at the barcode')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div 
              id="barcode-scanner" 
              ref={scannerContainerRef}
              className="w-full min-h-[300px] bg-gray-100 rounded-lg overflow-hidden"
            />
            <div className="flex gap-2">
              {!cameraStarted ? (
                <Button onClick={startCamera} className="flex-1" data-testid="start-camera">
                  <Camera className="w-4 h-4 mr-2" /> {t('scanner_ui.start_camera', 'Start Camera')}
                </Button>
              ) : (
                <Button variant="destructive" onClick={stopCamera} className="flex-1" data-testid="stop-camera">
                  {t('scanner_ui.stop_camera', 'Stop Camera')}
                </Button>
              )}
              <label className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <Button variant="outline" className="w-full" asChild>
                  <span><Upload className="w-4 h-4 mr-2" /> {t('scanner_ui.upload_image', 'Upload Image')}</span>
                </Button>
              </label>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderResultMode = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {scanResult?.product_name ? (
            <CheckCircle2 className="w-5 h-5 text-green-600" />
          ) : (
            <AlertCircle className="w-5 h-5 text-yellow-600" />
          )}
          {t('scanner_ui.scan_result', 'Scan Result')}
        </CardTitle>
        <CardDescription>
          {scanResult?.needs_enrichment 
            ? t('scanner_ui.product_found_needs_confirm', 'Product found but some fields need confirmation')
            : t('scanner_ui.product_identified', 'Product identified successfully')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Barcode Info */}
        <div className="p-4 bg-gray-50 rounded-lg space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">{t('scanner_ui.barcode', 'Barcode')}</span>
            <code className="font-mono text-sm bg-white px-2 py-1 rounded">
              {scanResult?.raw_code}
            </code>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">{t('scanner_ui.format', 'Format')}</span>
            <Badge variant="secondary">{scanResult?.format}</Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">{t('scanner_ui.confidence', 'Confidence')}</span>
            <Badge 
              variant={scanResult?.confidence > 0.7 ? 'success' : 'warning'}
              className={scanResult?.confidence > 0.7 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}
            >
              {Math.round((scanResult?.confidence || 0) * 100)}%
            </Badge>
          </div>
        </div>

        {/* Product Info */}
        {scanResult?.product_name && (
          <div className="p-4 border rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-gray-500" />
              <span className="font-medium">{scanResult.product_name}</span>
            </div>
            {scanResult.brand && (
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">{scanResult.brand}</span>
              </div>
            )}
            {scanResult.category && (
              <Badge variant="outline">{scanResult.category}</Badge>
            )}
          </div>
        )}

        {/* Price/Weight Info */}
        {(scanResult?.price || scanResult?.weight_lb) && (
          <div className="p-4 border rounded-lg space-y-2">
            {scanResult.price && (
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-gray-500" />
                <span className="font-medium">${scanResult.price.toFixed(2)}</span>
              </div>
            )}
            {scanResult.weight_lb && (
              <div className="text-sm text-gray-600">
                {t('scanner_ui.weight', 'Weight:')} {scanResult.weight_lb.toFixed(2)} lb
              </div>
            )}
            {scanResult.expiration_date && (
              <div className="text-sm text-gray-600">
                {t('scanner_ui.expires', 'Expires:')} {scanResult.expiration_date}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-4">
          <Button variant="outline" onClick={handleReset} className="flex-1" data-testid="scan-another">
            {t('scanner_ui.scan_another', 'Scan Another')}
          </Button>
          <Button 
            onClick={handleAddToQueue} 
            className="flex-1" 
            data-testid="add-to-queue"
          >
            <Package className="w-4 h-4 mr-2" />
            {t('scanner_ui.add_to_queue', 'Add to Queue')} ({scanQueue.length + 1})
          </Button>
        </div>
        
        {/* Minimum items notice */}
        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {t('scanner_ui.min_items_notice', `Minimum ${MIN_ITEMS_REQUIRED} items required to post deals. Current queue: ${scanQueue.length} item(s).`)}
          </p>
        </div>
      </CardContent>
    </Card>
  );

  const renderManualMode = () => (
    <ProductEntryForm 
      onCancel={() => setMode('scan')}
      initialData={scanResult}
      onSuccess={handleReset}
    />
  );

  const renderFormMode = () => (
    <ProductEntryForm 
      onCancel={handleReset}
      initialData={scanResult}
      barcodeResult={scanResult}
      onSuccess={handleReset}
    />
  );

  // Render batch confirmation grid when queue has items
  const renderBatchConfirmMode = () => (
    <BatchConfirmationGrid
      items={scanQueue}
      onSubmit={handleSubmitQueue}
      onCancel={() => {
        setMode('scan');
      }}
      loading={loading}
      title={t('scanner_ui.review_queue', 'Review Items to Post')}
      submitLabel={t('scanner_ui.post_all_deals', 'Post All Deals')}
    />
  );

  // Render queue panel using modular ScanQueue component
  const renderQueuePanel = () => (
    <ScanQueue
      items={scanQueue}
      minItemsRequired={MIN_ITEMS_REQUIRED}
      onReviewClick={() => setMode('batch-confirm')}
      onClearQueue={handleClearQueue}
      onRemoveItem={handleRemoveFromQueue}
      t={t}
    />
  );

  // Handle batch scan complete
  const handleBatchComplete = (successfulScans) => {
    // Refresh history after batch
    fetchRecentScans();
    fetchScanStats();
  };

  return (
    <RetailerLayout user={user} onLogout={onLogout}>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-testid="barcode-scanner-page">
        {/* Main Content */}
        <div className="lg:col-span-3">
          <div className="mb-6 flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  {t('retailer.scanner.title', 'Barcode Scanner')}
                </h1>
                <HelpTooltip portal="retailer" category="scanner" tooltipKey="main" size="md" />
              </div>
              <p className="text-gray-600">
                {t('retailer.scanner.subtitle', 'Scan products to add them as sizzling hot deals')}
              </p>
            </div>
            
            {/* Header Buttons: Demo Mode only (Tutorial moved to nav) */}
            <div className="flex gap-2">
              {/* Demo Mode Button */}
              <Button
                variant={demoModeActive ? 'default' : 'outline'}
                size="sm"
                onClick={activateDemoMode}
                disabled={demoModeActive}
                className={`flex items-center gap-2 ${demoModeActive ? 'bg-purple-600 hover:bg-purple-700' : 'border-purple-300 text-purple-600 hover:bg-purple-50'}`}
                data-testid="demo-mode-btn"
              >
                <Zap className="w-4 h-4" />
                {demoModeActive ? 'Demo Mode Active' : 'Demo Mode'}
              </Button>
            </div>
          </div>

          {/* Demo Mode Active Banner */}
          {demoModeActive && (
            <div className="mb-4 p-3 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="bg-purple-100 p-1.5 rounded-full">
                  <Zap className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-purple-900">Demo Mode Active</p>
                  <p className="text-xs text-purple-600">Sample products loaded. Click "Review & Post" to see the posting flow.</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearQueue}
                className="text-purple-600 hover:text-purple-800 hover:bg-purple-100"
              >
                Exit Demo
              </Button>
            </div>
          )}

          {/* GuidedWalkthrough moved to RetailerLayout - triggered via Tutorial nav button */}

          {/* Error display using modular component */}
          <ScanErrorAlert 
            error={error} 
            onDismiss={() => setError(null)}
            t={t}
          />

          {/* Queue panel - show when items are queued */}
          {(mode === 'scan' || mode === 'camera' || mode === 'result') && renderQueuePanel()}

          {/* Batch confirm mode - review and submit queue */}
          {mode === 'batch-confirm' ? (
            renderBatchConfirmMode()
          ) : (mode === 'scan' || mode === 'camera') ? (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-4 lg:grid-cols-7 mb-4 h-auto">
                <TabsTrigger value="single" data-testid="tab-single" className="py-2">
                  <ScanBarcode className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">{t('barcode.single_scan', 'Single')}</span>
                </TabsTrigger>
                <TabsTrigger value="continuous" data-testid="tab-continuous" className="py-2">
                  <Zap className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">{t('barcode.continuous_scan', 'Continuous')}</span>
                </TabsTrigger>
                <TabsTrigger value="shelf" data-testid="tab-shelf" className="py-2">
                  <ImagePlus className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">{t('barcode.shelf_scan', 'Photo')}</span>
                </TabsTrigger>
                <TabsTrigger value="multi-image" data-testid="tab-multi-image" className="py-2">
                  <Images className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">{t('barcode.multi_image', 'Multi')}</span>
                </TabsTrigger>
                <TabsTrigger value="video" data-testid="tab-video" className="py-2">
                  <Video className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">{t('barcode.video_scan', 'Video')}</span>
                </TabsTrigger>
                <TabsTrigger value="bluetooth" data-testid="tab-bluetooth" className="py-2">
                  <Bluetooth className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">{t('barcode.rapid_scan', 'Scanner')}</span>
                </TabsTrigger>
                <TabsTrigger value="batch" data-testid="tab-batch" className="py-2">
                  <Layers className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">{t('barcode.batch_scan', 'Batch')}</span>
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="single">
                {renderScanMode()}
              </TabsContent>
              
              <TabsContent value="continuous">
                <ContinuousScanMode 
                  onComplete={handleBatchComplete}
                  onCancel={() => setActiveTab('single')}
                />
              </TabsContent>
              
              <TabsContent value="shelf">
                <ShelfScanUpload 
                  onComplete={handleBatchComplete}
                  onCancel={() => setActiveTab('single')}
                />
              </TabsContent>
              
              <TabsContent value="multi-image">
                <MultiImageUpload 
                  onComplete={handleBatchComplete}
                  onCancel={() => setActiveTab('single')}
                />
              </TabsContent>
              
              <TabsContent value="video">
                <VideoScanUpload 
                  onComplete={handleBatchComplete}
                  onCancel={() => setActiveTab('single')}
                />
              </TabsContent>
              
              <TabsContent value="bluetooth">
                <RapidScanInput 
                  onComplete={handleBatchComplete}
                  onCancel={() => setActiveTab('single')}
                />
              </TabsContent>
              
              <TabsContent value="batch">
                <BatchScanner onBatchComplete={handleBatchComplete} />
              </TabsContent>
            </Tabs>
          ) : (
            <>
              {mode === 'result' && renderResultMode()}
              {mode === 'manual' && renderManualMode()}
              {mode === 'form' && renderFormMode()}
            </>
          )}
        </div>

        {/* Sidebar - Recent Scans & Stats */}
        <div className="lg:col-span-1 space-y-4">
          {renderScanStats()}
          {renderRecentScans()}
        </div>
      </div>
    </RetailerLayout>
  );
}
